import { useState, useCallback, useEffect } from "react";
import classNames from "classnames";

import AscTextInput from "../../../AscInputFields/AscTextInput/AscTextInput";
import AscParagraph from "../../../AscTypography/AscParagraph/AscParagraph";
import AscRichTextEditor from "../../../AscRichTextEditor/AscRichTextEditor";
import AscLinkInput from "../../../AscInputFields/AscLinkInput/AscLinkInput";
import AscModal from "../../../AscModal/AscModal";
import AscFilesUploader from "../../../AscFilesUploader/AscFilesUploader";
import AscFeedbackPreviewField from "../AscFeedbackPreviewField/AscFeedbackPreviewField";
import AscButton from "../../../AscButton/AscButton";
import { AdminEditPictureIcon, AdminImageIcon } from "../../../AscIcons";

import styles from "./AscAddApps.module.scss";

function AscApp({
  formik,
  currentIndex,
}: {
  formik: any;
  currentIndex: number;
}) {
  const [uploadModal, setIsModalOpen] = useState(false);

  const handleUpload = useCallback(
    (acceptedFiles: any[]) => {
      formik.setFieldValue(`app[${currentIndex}].image`, acceptedFiles[0]);
      setIsModalOpen(false);
    },
    [currentIndex]
  );

  return (
    <>
      <div className={styles.imageGround}>
        {formik.values.app[currentIndex]?.image ? (
          <img
            src={
              typeof formik.values.app[currentIndex]?.image === "string"
                ? formik.values.app[currentIndex]?.image
                : URL.createObjectURL(formik.values.app[currentIndex]?.image)
            }
            alt="app_image"
            className={styles.image}
          />
        ) : (
          <AdminImageIcon className={styles.pictureIcon} />
        )}
        <div
          className={classNames(
            styles.editPictureBlock,
            !formik.values.app[currentIndex]?.image &&
              styles.editPictureBlockNoImage
          )}
          onClick={() => setIsModalOpen(true)}
        >
          <AdminEditPictureIcon className={styles.editPicture} />
        </div>
        {formik.errors.app?.[currentIndex]?.image && (
          <div className={styles.fieldError}>
            {formik.errors.app?.[currentIndex]?.image}
          </div>
        )}
      </div>

      <div className={styles.middleGround}>
        <div className={styles.form}>
          <label className={styles.fieldLabel} htmlFor="name">
            App name
          </label>
          <AscTextInput
            className={classNames(styles.inputField)}
            fullWidth={true}
            placeholder="Name of app"
            id={`app[${currentIndex}].name`}
            name={`app[${currentIndex}].name`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.app[currentIndex]?.name}
          />
        </div>

        <AscParagraph className={styles.title}>Description </AscParagraph>

        <div className={styles.textEditorGround}>
          <AscRichTextEditor
            editable={true}
            name={`app[${currentIndex}].description`}
            initialHtmlValue={formik.values.app[currentIndex]?.description}
            onHtmlChange={(val) =>
              formik.setFieldValue(`app[${currentIndex}].description`, val)
            }
          />
        </div>

        <div className={styles.form}>
          <label className={styles.fieldLabel} htmlFor="iosLink">
            Links
          </label>
          <AscLinkInput
            fullWidth={true}
            variant="ios"
            placeholder="Link"
            id={`app[${currentIndex}].iosLink`}
            name={`app[${currentIndex}].iosLink`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.app[currentIndex]?.iosLink}
          />

          <AscLinkInput
            fullWidth={true}
            variant="android"
            placeholder="Link"
            id={`app[${currentIndex}].androidLink`}
            name={`app[${currentIndex}].androidLink`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.app[currentIndex]?.androidLink}
          />
        </div>
      </div>

      {uploadModal && (
        <AscModal
          visible={uploadModal}
          onClose={() => setIsModalOpen(false)}
          innerContentClassName={styles.uploadImageModalContent}
        >
          <AscFilesUploader
            name="image"
            type="image"
            accept={{
              "image/*": [".jpeg", ".png"],
            }}
            multiple={false}
            onDrop={handleUpload}
            srcFile=""
            className={styles.image}
          />
        </AscModal>
      )}
    </>
  );
}

export interface AscAddAppsProps {
  className?: string;
  formik?: any;
  deleteFeedbackApp?: (appId: string) => Promise<void>;
}

export default function AscAddApps({
  className,
  formik,
  deleteFeedbackApp,
}: AscAddAppsProps) {
  const objInit = {
    name: "",
    description: "",
    iosLink: "",
    androidLink: "",
    image: "",
    create: true,
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [appLength, setAppLength] = useState(formik.values.app.length);

  const onDelete = (appIndex: number, appId?: string) => {
    if (appId && deleteFeedbackApp) {
      deleteFeedbackApp(appId);
    }
    formik.setFieldValue(
      "app",
      formik.values.app.filter(
        (app: any, index: number) => index !== appIndex && app
      )
    );

    if (appLength > 0) {
      setAppLength((prev: number) => prev - 1);
      setCurrentIndex(0);
    }
  };

  useEffect(() => {
    if (appLength === 0 || appLength < 0) {
      formik.setFieldValue(`app[0]`, objInit);
      setCurrentIndex(0);
      setAppLength(1);
    }
  }, [appLength]);

  return (
    <div className={classNames(styles.ground, className)}>
      <AscApp formik={formik} currentIndex={currentIndex} key={currentIndex} />

      <div className={styles.previewSection}>
        {formik.values.app?.map((appItem: any, index: number) => {
          return (
            appItem && (
              <AscFeedbackPreviewField
                title={appItem?.name || "Start typing name"}
                key={appItem?.name}
                onEditClick={() => setCurrentIndex(index)}
                onDeleteClick={() => onDelete(index, appItem?.id)}
                selected={currentIndex === index}
                hasError={
                  formik.errors?.app &&
                  formik.errors?.app?.length > 0 &&
                  formik.errors?.app[index]
                }
              />
            )
          );
        })}

        <AscButton
          className={styles.button}
          variant="secondary"
          onClick={() => {
            setAppLength((prev: number) => prev + 1);
            setCurrentIndex(appLength);
            formik.setFieldValue(`app[${appLength}]`, objInit);
          }}
        >
          + Add app
        </AscButton>
      </div>
    </div>
  );
}
